import React, { useMemo, useState, useCallback } from "react";
import useSWR from "swr";

import { ReasonColumn } from "./ReasonColumn";
import { useNotification } from "../../Notifications/NotificationProvider";
import { fetcher } from "../../../utilities/_fetcher";
import { getLoanReasonsUrl, saveLoanReasons } from "../service/loanService";

const ShimmerSkeleton = () => (
  <div className="shimmer-skeleton">
    {[...Array(4)].map((_, colIndex) => (
      <div key={colIndex} className="shimmer-column">
        <div className="shimmer-header"></div>
        {[...Array(6)].map((_, rowIndex) => (
          <div key={rowIndex} className="shimmer-row"></div>
        ))}
      </div>
    ))}
  </div>
);

const ActionReasonsModal = ({ onClose }) => {
  const [columns, setColumns] = useState([]);
  const [original, setOriginal] = useState([]);
  const [saving, setSaving] = useState(false);
  const dispatchNotification = useNotification();

  const apiUrl = getLoanReasonsUrl();

  const {
    data: apiReasons,
    error,
    mutate: mutateReasons,
  } = useSWR(apiUrl, fetcher, {
    onSuccess: (data) => {
      const transformedColumns = transformApiDataToColumns(data || []);
      setColumns(transformedColumns);
      setOriginal(transformedColumns);
    },
    onError: (error) => {
      console.error("Failed to fetch loan reasons:", error);
      const transformedColumns = transformApiDataToColumns([]);
      setColumns(transformedColumns);
      setOriginal(transformedColumns);

      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to fetch loan reasons";

      dispatchNotification({
        type: "ERROR",
        message: errorMessage,
      });
    },
  });

  const loading = !apiReasons && !error;

  const transformApiDataToColumns = useCallback((apiReasons) => {
    const columnMap = {
      "mdo-accept": {
        id: "mdo-accept",
        title: "MDO Approval Reasons",
        reasons: [],
      },
      "mdo-reject": {
        id: "mdo-reject",
        title: "MDO Rejection Reasons",
        reasons: [],
      },
      "om-accept": {
        id: "om-accept",
        title: "OM Approval Reasons",
        reasons: [],
      },
      "om-reject": {
        id: "om-reject",
        title: "OM Rejection Reasons",
        reasons: [],
      },
    };

    apiReasons.forEach((apiReason) => {
      const columnKey = `${apiReason.user_type}-${apiReason.reason_type}`;
      if (columnMap[columnKey]) {
        columnMap[columnKey].reasons.push({
          id: apiReason.reason_id.toString(),
          text: apiReason.reason,
          deactivated: !apiReason.is_active,
          isFromApi: true,
        });
      }
    });

    return Object.values(columnMap);
  }, []);

  const generateModifications = useCallback(() => {
    const modifications = [];

    columns.forEach((column) => {
      const [userType, reasonType] = column.id.split("-");
      const originalReasons =
        original.find((c) => c.id === column.id)?.reasons || [];

      column.reasons.forEach((reason) => {
        const originalReason = originalReasons.find((r) => r.id === reason.id);

        if (!reason.isFromApi) {
          modifications.push({
            reason_id: null,
            reason: reason.text,
            command: "add",
            user_type: userType,
            reason_type: reasonType,
          });
        } else if (originalReason) {
          if (originalReason.deactivated !== reason.deactivated) {
            modifications.push({
              reason_id: reason.id,
              reason: reason.text,
              command: reason.deactivated ? "deactivate" : "activate",
              user_type: userType,
              reason_type: reasonType,
            });
          }
        }
      });
    });

    return modifications;
  }, [columns, original]);

  const hasChanges = useMemo(
    () => JSON.stringify(columns) !== JSON.stringify(original),
    [columns, original]
  );

  const updateColumn = useCallback((id, reasons) => {
    setColumns((prev) =>
      prev.map((c) => (c.id === id ? { ...c, reasons } : c))
    );
  }, []);

  const onCancel = useCallback(() => {
    onClose();
    setColumns(original);
  }, [onClose, original]);

  const onSave = useCallback(async () => {
    try {
      setSaving(true);
      const modifications = generateModifications();

      if (modifications.length > 0) {
        await saveLoanReasons({ modifications });

        mutateReasons();

        dispatchNotification({
          type: "SUCCESS",
          message: "Loan reasons updated successfully",
        });
      } else {
        dispatchNotification({
          type: "INFO",
          message: "No changes to save",
        });
      }

      setOriginal(columns);
      onClose();
    } catch (error) {
      console.error("Failed to save loan reasons:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to save changes. Please try again.";

      dispatchNotification({
        type: "ERROR",
        message: errorMessage,
      });
    } finally {
      setSaving(false);
    }
  }, [
    generateModifications,
    columns,
    onClose,
    mutateReasons,
    dispatchNotification,
  ]);

  return (
    <div className="approval-modal-overlay">
      <div className="reason-create-modal">
        <div className="reason-create-modal__content">
          {loading || columns.length === 0 ? (
            <ShimmerSkeleton />
          ) : (
            <div className="reasons-grid">
              {columns.map((c) => (
                <ReasonColumn
                  key={c.id}
                  column={c}
                  onChange={(r) => updateColumn(c.id, r)}
                />
              ))}
            </div>
          )}
        </div>
        <footer className="reason-create-modal__footer">
          <button
            className="reason-modal-btn reason-modal-btn__cancel"
            onClick={onCancel}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            className="reason-modal-btn reason-modal-btn__save"
            onClick={onSave}
            disabled={!hasChanges || saving || loading}
          >
            {saving ? "Saving..." : "Save changes"}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default ActionReasonsModal;
